// src/pages/Firms.js
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import api from '../lib/api'
import MergeModal from '../components/MergeModal'

// ── Constants ─────────────────────────────────────────────────────────────

const FIRM_TYPES = ['GP Client', 'LP / Investor', 'Partner Advisor', 'Service Provider', 'Other']

const FUND_STRATEGIES = [
  'Early Stage Venture',
  'Late Stage Venture',
  'Growth Equity',
  'Private Equity',
  'Buyout',
  'Real Estate',
  'Sports',
  'Distressed',
  'Credit',
]

const SECTORS = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Consumer',
  'Industrial',
  'Energy',
  'Real Estate',
  'Media & Entertainment',
  'Infrastructure',
  'Business Services',
  'Education',
  'Other',
]

const FIRM_TYPE_BADGE = {
  'GP Client':       'badge-green',
  'LP / Investor':   'badge-blue',
  'Partner Advisor': 'badge-purple',
  'Service Provider':'badge-gray',
  'Other':           'badge-gray',
}

const CONTACT_TYPE_BADGE = {
  'Investor': 'badge-blue', 'GP': 'badge-green',
  'Partner Advisor': 'badge-purple', 'Service Provider': 'badge-gray', 'RGA Team': 'badge-yellow',
}

const TAG_COLORS = ['badge-blue','badge-green','badge-purple','badge-yellow','badge-red','badge-gray']
function tagColor(t) { return TAG_COLORS[Math.abs([...t].reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length] }

const AUM_RANGES = [
  { label: '< $100M',       test: v => v != null && v < 100e6 },
  { label: '$100M-$500M',   test: v => v != null && v >= 100e6 && v <= 500e6 },
  { label: '$500M-$1B',     test: v => v != null && v > 500e6 && v <= 1e9 },
  { label: '$1B+',          test: v => v != null && v > 1e9 },
]

const SORTABLE_COLS = {
  name:      { label: 'Firm Name',       fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
  type:      { label: 'Type',            fn: (a, b) => ((a.firm_type ?? [])[0] || '').localeCompare((b.firm_type ?? [])[0] || '') },
  hq:        { label: 'HQ',              fn: (a, b) => (a.hq_location || '').localeCompare(b.hq_location || '') },
  aum:       { label: 'AUM / Fund Size', fn: (a, b) => (a.aum_fund_size ?? 0) - (b.aum_fund_size ?? 0) },
  notes:     { label: 'Notes',           fn: (a, b) => (a.notes || '').localeCompare(b.notes || '') },
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatAUM(n) {
  if (n == null) return null
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6)  return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}

function todayStr() { return new Date().toISOString().slice(0, 10) }

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 560, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>&#10005;</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

function SortHeader({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>{'▲'}</span>}
    </th>
  )
}

function MultiSelectFilter({ label, options, selected, onToggle, badgeMap }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 180, maxHeight: 260, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {options.map(o => {
            const checked = selected.includes(o)
            return (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: 'pointer', fontSize: 12,
                background: checked ? 'var(--accent-muted)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  style={{ accentColor: 'var(--accent)' }} />
                {badgeMap
                  ? <span className={`badge ${badgeMap[o] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{o}</span>
                  : <span className={`badge ${tagColor(o)}`} style={{ fontSize: 10 }}>{o}</span>
                }
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Pagination bar ────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, pageStart, pageSize, totalCount, onPage }) {
  const from = totalCount === 0 ? 0 : pageStart + 1
  const to   = Math.min(pageStart + pageSize, totalCount)

  // Build page-number window (up to 7 slots with ellipsis)
  function pageNums() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages = []
    pages.push(1)
    if (page > 4) pages.push('…')
    const start = Math.max(2, page - 2)
    const end   = Math.min(totalPages - 1, page + 2)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 3) pages.push('…')
    pages.push(totalPages)
    return pages
  }

  const btnBase = {
    minWidth: 30, height: 28, padding: '0 7px', borderRadius: 4,
    border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        Showing {from}–{to} of {totalCount} records
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          onClick={() => onPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >‹ Prev</button>

        {pageNums().map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>…</span>
            : <button
                key={n}
                onClick={() => onPage(n)}
                style={{
                  ...btnBase,
                  background: n === page ? 'var(--accent)' : 'var(--surface-raised)',
                  color: n === page ? '#fff' : 'var(--text-secondary)',
                  fontWeight: n === page ? 700 : 400,
                  borderColor: n === page ? 'var(--accent)' : 'var(--border)',
                }}
              >{n}</button>
        )}

        <button
          onClick={() => onPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >Next ›</button>
      </div>
    </div>
  )
}

// ── Export helpers ────────────────────────────────────────────────────────

function exportFirms(rows, users, format) {
  const resolveOwners = (ids) => {
    if (!ids || !ids.length) return ''
    return ids.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : id.slice(0, 8) + '...'
    }).join(', ')
  }

  const data = rows.map(f => ({
    'Name':               f.name ?? '',
    'Firm Type':          (f.firm_type ?? []).join(', '),
    'HQ Location':        f.hq_location ?? '',
    'AUM/Fund Size':      f.aum_fund_size ?? '',
    'Website':            f.website ?? '',
    'Tags':               (f.tags ?? []).join(', '),
    'Relationship Owner': resolveOwners(f.relationship_owner ?? []),
    'Contacts':           (f.contacts ?? []).map(c => c.name).join(', '),
  }))

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Firms')

  const filename = `RGA_Firms_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── Firm form ─────────────────────────────────────────────────────────────

function FirmForm({ initial = {}, users, onSubmit, onCancel, submitting, error }) {
  const [form, setForm] = useState({
    name:                        initial.name               ?? '',
    firm_type:                   initial.firm_type           ?? [],
    website:                     initial.website             ?? '',
    hq_location:                 initial.hq_location         ?? '',
    aum_fund_size:               initial.aum_fund_size != null ? String(initial.aum_fund_size) : '',
    tags:                       (initial.tags               ?? []).join(', '),
    relationship_owner:          initial.relationship_owner  ?? [],
    notes:                       initial.notes               ?? '',
    fund_strategies:             initial.fund_strategies             ?? [],
    sectors:                     initial.sectors                     ?? [],
    direct_investment_interest:  initial.direct_investment_interest  ?? false,
    direct_investment_areas:     initial.direct_investment_areas     ?? '',
  })
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    setForm(p => ({
      ...p,
      firm_type: p.firm_type.includes(t)
        ? p.firm_type.filter(x => x !== t)
        : [...p.firm_type, t],
    }))
  }

  function toggleArrayField(field, value) {
    setForm(p => ({
      ...p,
      [field]: p[field].includes(value)
        ? p[field].filter(x => x !== value)
        : [...p[field], value],
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim())    return setErr('Name is required.')
    if (!form.firm_type.length) return setErr('At least one firm type is required.')
    const payload = {
      name:      form.name.trim(),
      firm_type: form.firm_type,
    }
    if (form.website)            payload.website            = form.website.trim()
    if (form.hq_location)        payload.hq_location        = form.hq_location.trim()
    if (form.aum_fund_size)      payload.aum_fund_size      = parseFloat(form.aum_fund_size)
    if (form.tags.trim())        payload.tags               = form.tags.split(',').map(s => s.trim()).filter(Boolean)
    payload.relationship_owner = form.relationship_owner
    if (form.notes)              payload.notes              = form.notes.trim()
    payload.fund_strategies            = form.fund_strategies
    payload.sectors                    = form.sectors
    payload.direct_investment_interest = form.direct_investment_interest
    if (form.direct_investment_interest && form.direct_investment_areas.trim())
      payload.direct_investment_areas  = form.direct_investment_areas.trim()
    else
      payload.direct_investment_areas  = null
    try { await onSubmit(payload) }
    catch (err2) { setErr(err2.response?.data?.error || err2.message) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ErrorBanner msg={err || error} />

      {/* Name */}
      <div className="input-group">
        <label className="input-label">Firm Name *</label>
        <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="e.g. Halcyon Endowment Fund" autoFocus />
      </div>

      {/* Firm Type */}
      <div className="input-group">
        <label className="input-label">Firm Type *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {FIRM_TYPES.map(t => {
            const checked = form.firm_type.includes(t)
            return (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, padding: '5px 10px', borderRadius: 5,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                background: checked ? 'var(--accent-muted)' : 'transparent',
                color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'all 0.12s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleType(t)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            )
          })}
        </div>
      </div>

      {/* Website + HQ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Website</label>
          <input className="input" value={form.website} onChange={e => set('website', e.target.value)}
            placeholder="https://..." />
        </div>
        <div className="input-group">
          <label className="input-label">HQ Location</label>
          <input className="input" value={form.hq_location} onChange={e => set('hq_location', e.target.value)}
            placeholder="New York, NY" />
        </div>
      </div>

      {/* AUM */}
      <div className="input-group">
        <label className="input-label">AUM / Fund Size ($)</label>
        <input className="input" type="number" min="0" value={form.aum_fund_size}
          onChange={e => set('aum_fund_size', e.target.value)} placeholder="e.g. 4200000000" />
        {form.aum_fund_size !== '' && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            = {formatAUM(parseFloat(form.aum_fund_size))}
          </div>
        )}
      </div>

      {/* Relationship Owner — multi-select badges */}
      <div className="input-group">
        <label className="input-label">Relationship Owner</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {(users ?? []).map(u => {
            const on = form.relationship_owner.includes(u.id)
            return (
              <button key={u.id} type="button"
                onClick={() => toggleArrayField('relationship_owner', u.id)}
                style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 12, cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent)' : 'transparent',
                  color: on ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.12s',
                }}>{u.name}</button>
            )
          })}
        </div>
      </div>

      {/* Tags */}
      <div className="input-group">
        <label className="input-label">Tags</label>
        <input className="input" value={form.tags} onChange={e => set('tags', e.target.value)}
          placeholder="endowment, buyout, North America - comma-separated" />
      </div>

      {/* Notes */}
      <div className="input-group">
        <label className="input-label">Notes</label>
        <textarea className="textarea" rows={2} value={form.notes}
          onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
      </div>

      {/* Investment Profile */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 2 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 14 }}>
          Investment Profile
        </div>

        <div className="input-group" style={{ marginBottom: 14 }}>
          <label className="input-label">Fund Strategies of Interest</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {FUND_STRATEGIES.map(s => {
              const on = form.fund_strategies.includes(s)
              return (
                <button key={s} type="button" onClick={() => toggleArrayField('fund_strategies', s)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.12s',
                  }}>{s}</button>
              )
            })}
          </div>
        </div>

        <div className="input-group" style={{ marginBottom: 14 }}>
          <label className="input-label">Sectors</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {SECTORS.map(s => {
              const on = form.sectors.includes(s)
              return (
                <button key={s} type="button" onClick={() => toggleArrayField('sectors', s)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.12s',
                  }}>{s}</button>
              )
            })}
          </div>
        </div>

        <div className="input-group" style={{ marginBottom: form.direct_investment_interest ? 14 : 0 }}>
          <label className="input-label">Direct Investment Interest</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {[true, false].map(val => (
              <button key={String(val)} type="button"
                onClick={() => set('direct_investment_interest', val)}
                style={{
                  fontSize: 12, padding: '5px 16px', borderRadius: 5, cursor: 'pointer',
                  border: `1px solid ${form.direct_investment_interest === val ? 'var(--accent)' : 'var(--border)'}`,
                  background: form.direct_investment_interest === val ? 'var(--accent)' : 'transparent',
                  color: form.direct_investment_interest === val ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.12s',
                }}>{val ? 'Yes' : 'No'}</button>
            ))}
          </div>
        </div>

        {form.direct_investment_interest && (
          <div className="input-group">
            <label className="input-label">Direct Investment Areas of Interest</label>
            <textarea className="textarea" rows={3} value={form.direct_investment_areas}
              onChange={e => set('direct_investment_areas', e.target.value)}
              placeholder="Describe areas of direct investment interest..." />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// ── Firm detail modal ─────────────────────────────────────────────────────

function FirmDetailModal({ firm, onClose, onUpdated, users }) {
  const [editMode, setEditMode] = useState(false)
  const [fundStrategies, setFundStrategies] = useState(firm.fund_strategies ?? [])
  const [sectors, setSectors]               = useState(firm.sectors ?? [])
  const [directInterest, setDirectInterest] = useState(firm.direct_investment_interest ?? false)
  const [directAreas, setDirectAreas]       = useState(firm.direct_investment_areas ?? '')
  const qc = useQueryClient()

  const { data: contactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', { firm_id: firm.firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: firm.firm_id } }).then(r => r.data.data ?? []),
  })
  const firmContacts = contactsData ?? []

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/firms/${firm.firm_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setEditMode(false)
      onUpdated?.()
    },
  })

  function patchField(payload) {
    api.patch(`/api/firms/${firm.firm_id}`, payload)
      .then(() => qc.invalidateQueries({ queryKey: ['firms'] }))
  }

  function toggleStrategy(s) {
    const next = fundStrategies.includes(s)
      ? fundStrategies.filter(x => x !== s)
      : [...fundStrategies, s]
    setFundStrategies(next)
    patchField({ fund_strategies: next })
  }

  function toggleSector(s) {
    const next = sectors.includes(s)
      ? sectors.filter(x => x !== s)
      : [...sectors, s]
    setSectors(next)
    patchField({ sectors: next })
  }

  function toggleDirectInterest(val) {
    setDirectInterest(val)
    patchField({ direct_investment_interest: val })
  }

  const ownerNames = (firm.relationship_owner ?? []).map(id => {
    const u = (users ?? []).find(u => u.id === id)
    return u ? u.name : id.slice(0, 8) + '...'
  }).join(', ') || null

  if (editMode) {
    return (
      <Modal title={`Edit - ${firm.name}`} onClose={onClose} width={580}>
        <FirmForm
          initial={firm}
          users={users}
          onSubmit={updateMutation.mutateAsync}
          onCancel={() => setEditMode(false)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.response?.data?.error}
        />
      </Modal>
    )
  }

  return (
    <Modal title={firm.name} onClose={onClose} width={580}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{firm.name}</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {(firm.firm_type ?? []).map(t => (
                <span key={t} className={`badge ${FIRM_TYPE_BADGE[t] ?? 'badge-gray'}`}>{t}</span>
              ))}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>Edit</button>
        </div>

        <div className="divider" />

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="HQ Location">{firm.hq_location}</Field>
          <Field label="AUM / Fund Size">{formatAUM(firm.aum_fund_size)}</Field>
          <Field label="Website">
            {firm.website
              ? <a href={firm.website} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--accent)' }}>{firm.website}</a>
              : null}
          </Field>
          <Field label="Relationship Owner">{ownerNames}</Field>
          {(firm.tags ?? []).length > 0 && (
            <Field label="Tags">
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {firm.tags.map(t => <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>)}
              </div>
            </Field>
          )}
          {firm.notes && <Field label="Notes">{firm.notes}</Field>}
        </div>

        {/* Investment Profile */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 14 }}>
            Investment Profile
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Fund Strategies of Interest
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {FUND_STRATEGIES.map(s => {
                  const on = fundStrategies.includes(s)
                  return (
                    <button key={s} type="button" onClick={() => toggleStrategy(s)}
                      style={{
                        fontSize: 11, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                        background: on ? 'var(--accent)' : 'transparent',
                        color: on ? '#fff' : 'var(--text-secondary)',
                        transition: 'all 0.12s',
                      }}>{s}</button>
                  )
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Sectors
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SECTORS.map(s => {
                  const on = sectors.includes(s)
                  return (
                    <button key={s} type="button" onClick={() => toggleSector(s)}
                      style={{
                        fontSize: 11, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                        background: on ? 'var(--accent)' : 'transparent',
                        color: on ? '#fff' : 'var(--text-secondary)',
                        transition: 'all 0.12s',
                      }}>{s}</button>
                  )
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Direct Investment Interest
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[true, false].map(val => (
                  <button key={String(val)} type="button"
                    onClick={() => toggleDirectInterest(val)}
                    style={{
                      fontSize: 12, padding: '5px 16px', borderRadius: 5, cursor: 'pointer',
                      border: `1px solid ${directInterest === val ? 'var(--accent)' : 'var(--border)'}`,
                      background: directInterest === val ? 'var(--accent)' : 'transparent',
                      color: directInterest === val ? '#fff' : 'var(--text-secondary)',
                      transition: 'all 0.12s',
                    }}>{val ? 'Yes' : 'No'}</button>
                ))}
              </div>
            </div>

            {directInterest && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Direct Investment Areas of Interest
                </div>
                <textarea
                  className="textarea"
                  rows={3}
                  value={directAreas}
                  onChange={e => setDirectAreas(e.target.value)}
                  onBlur={() => patchField({ direct_investment_areas: directAreas.trim() || null })}
                  placeholder="Describe areas of direct investment interest..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Associated contacts */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Contacts ({firmContacts.length})
          </div>
          {contactsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <div className="spinner" />
            </div>
          ) : firmContacts.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts linked to this firm.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {firmContacts.map(c => (
                <div key={c.contact_id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '9px 12px', background: 'var(--surface-raised)',
                  borderRadius: 6, border: '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    {c.title_role && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{c.title_role}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(c.contact_type ?? []).slice(0, 2).map(t => (
                      <span key={t} className={`badge ${CONTACT_TYPE_BADGE[t] ?? 'badge-gray'}`}
                        style={{ fontSize: 10 }}>{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Firms() {
  const [search, setSearch]           = useState('')
  const [typeFilter, setTypeFilter]   = useState([])
  const [tagFilter, setTagFilter]     = useState([])
  const [ownerFilter, setOwnerFilter] = useState('')
  const [aumFilter, setAumFilter]     = useState('')
  const [directInvestmentFilter, setDirectInvestmentFilter] = useState(false)
  const [sortCol, setSortCol]         = useState('name')
  const [sortDir, setSortDir]         = useState('asc')
  const [page, setPage]               = useState(1)
  const [pageSize, setPageSize]       = useState(20)
  const [selected, setSelected]       = useState(null)
  const [createOpen, setCreate]       = useState(false)
  const [toDelete, setToDelete]       = useState(null)
  // ── merge / bulk-delete state ────────────────────────────────────────────
  const [checkedIds, setCheckedIds]   = useState(new Set())
  const [mergeOpen, setMergeOpen]     = useState(false)
  const [mergePrimaryId, setMergePrimaryId] = useState(null)
  const [mergeError, setMergeError]   = useState(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting]     = useState(false)
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null) // { succeeded, failed }
  const qc = useQueryClient()

  const { data: firmsData, isLoading } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  const firms = useMemo(() => firmsData ?? [], [firmsData])
  const users = useMemo(() => usersData ?? [], [usersData])

  const allTags = useMemo(() => {
    const s = new Set()
    for (const f of firms) { for (const t of (f.tags ?? [])) s.add(t) }
    return Array.from(s).sort()
  }, [firms])

  const ownerName = useCallback((ids) => {
    const arr = Array.isArray(ids) ? ids : (ids ? [ids] : [])
    if (!arr.length) return '—'
    const names = arr.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : id.slice(0, 8) + '...'
    })
    if (names.length <= 2) return names.join(', ')
    return names.slice(0, 2).join(', ') + ` +${names.length - 2} more`
  }, [users])

  function toggleTypeFilter(t) { setTypeFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleTagFilter(t) { setTagFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }

  const hasFilters = Boolean(search || typeFilter.length || tagFilter.length || ownerFilter || aumFilter || directInvestmentFilter)
  const activeFilterCount = (typeFilter.length ? 1 : 0) + (tagFilter.length ? 1 : 0) + (ownerFilter ? 1 : 0) + (aumFilter ? 1 : 0) + (directInvestmentFilter ? 1 : 0)

  function clearAllFilters() {
    setSearch(''); setTypeFilter([]); setTagFilter([]); setOwnerFilter(''); setAumFilter(''); setDirectInvestmentFilter(false)
    setPage(1)
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let list = firms.filter(f => {
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false
      if (typeFilter.length && !typeFilter.some(t => f.firm_type?.includes(t))) return false
      if (tagFilter.length && !tagFilter.some(t => (f.tags ?? []).includes(t))) return false
      if (ownerFilter && !(f.relationship_owner ?? []).includes(ownerFilter)) return false
      if (aumFilter) {
        const range = AUM_RANGES.find(r => r.label === aumFilter)
        if (range && !range.test(f.aum_fund_size)) return false
      }
      if (directInvestmentFilter && !f.direct_investment_interest) return false
      return true
    })

    const sortFn = SORTABLE_COLS[sortCol]?.fn
    if (sortFn) {
      list = [...list].sort((a, b) => sortDir === 'asc' ? sortFn(a, b) : sortFn(b, a))
    }
    return list
  }, [firms, search, typeFilter, tagFilter, ownerFilter, aumFilter, directInvestmentFilter, sortCol, sortDir])

  // Reset to page 1 whenever filters or sort change
  useEffect(() => { setPage(1) }, [search, typeFilter, tagFilter, ownerFilter, aumFilter, directInvestmentFilter, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage   = Math.min(page, totalPages)
  const pageStart  = (safePage - 1) * pageSize        // 0-based
  const paginated  = filtered.slice(pageStart, pageStart + pageSize)

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/firms', payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['firms'] }); setCreate(false) },
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/firms/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setToDelete(null)
      setSelected(prev => prev?.firm_id === id ? null : prev)
    },
  })

  const mergeMutation = useMutation({
    mutationFn: ({ primary_id, merge_ids }) =>
      api.post('/api/firms/merge', { primary_id, merge_ids }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setCheckedIds(new Set())
      setMergeOpen(false)
      setMergeError(null)
    },
    onError: err => setMergeError(err.response?.data?.error || err.message),
  })

  // ── checkbox helpers ─────────────────────────────────────────────────────
  function toggleCheck(id, e) {
    e.stopPropagation()
    setCheckedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleCheckAll() {
    const pageIds = paginated.map(f => f.firm_id)
    const allPageChecked = pageIds.every(id => checkedIds.has(id))
    if (allPageChecked) {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n })
    } else {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n })
    }
  }

  const checkedRecords = useMemo(
    () => firms.filter(f => checkedIds.has(f.firm_id)),
    [firms, checkedIds]
  )

  function openMerge() {
    // Default primary = oldest created_at among checked
    const sorted = [...checkedRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    setMergePrimaryId(sorted[0]?.firm_id ?? null)
    setMergeError(null)
    setMergeOpen(true)
  }

  async function bulkDeleteFirms() {
    setBulkDeleting(true)
    setBulkDeleteResult(null)
    let succeeded = 0, failed = 0
    for (const id of checkedIds) {
      try {
        await api.delete(`/api/firms/${id}`)
        succeeded++
      } catch {
        failed++
      }
    }
    await qc.invalidateQueries({ queryKey: ['firms'] })
    setCheckedIds(new Set())
    setBulkDeleting(false)
    setBulkDeleteOpen(false)
    if (failed > 0) setBulkDeleteResult({ succeeded, failed })
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Firms</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isLoading ? 'Loading...' : `${firms.length} firm${firms.length !== 1 ? 's' : ''}`}
            {checkedIds.size > 0 && (
              <button
                onClick={() => setCheckedIds(new Set())}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontSize: 12, color: 'var(--text-secondary)',
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Clear {checkedIds.size} selected
              </button>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {checkedIds.size >= 2 && (
            <button className="btn btn-secondary" onClick={openMerge}
              style={{ fontWeight: 600 }}>
              Merge Selected ({checkedIds.size})
            </button>
          )}
          {checkedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkDeleteResult(null); setBulkDeleteOpen(true) }}>
              Delete Selected ({checkedIds.size})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => exportFirms(filtered, users, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportFirms(filtered, users, 'xlsx')}>Export Excel</button>
          <button className="btn btn-primary" onClick={() => setCreate(true)}>+ New Firm</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name..."
          value={search} onChange={e => setSearch(e.target.value)} />

        <MultiSelectFilter label="Firm Type" options={FIRM_TYPES}
          selected={typeFilter} onToggle={toggleTypeFilter} badgeMap={FIRM_TYPE_BADGE} />

        {allTags.length > 0 && (
          <MultiSelectFilter label="Tags" options={allTags}
            selected={tagFilter} onToggle={toggleTagFilter} />
        )}

        <select className="select" style={{ maxWidth: 180, fontSize: 12 }} value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}>
          <option value="">All owners</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={aumFilter}
          onChange={e => setAumFilter(e.target.value)}>
          <option value="">AUM range...</option>
          {AUM_RANGES.map(r => <option key={r.label} value={r.label}>{r.label}</option>)}
        </select>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          fontSize: 12, color: directInvestmentFilter ? 'var(--text-primary)' : 'var(--text-secondary)',
          padding: '4px 10px', borderRadius: 5,
          border: `1px solid ${directInvestmentFilter ? 'var(--accent)' : 'var(--border)'}`,
          background: directInvestmentFilter ? 'var(--accent-muted)' : 'transparent',
          userSelect: 'none',
        }}>
          <input type="checkbox" checked={directInvestmentFilter}
            onChange={e => setDirectInvestmentFilter(e.target.checked)}
            style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
          Direct investors only
        </label>

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Clear all{activeFilterCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', borderRadius: 8,
                padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {filtered.length !== firms.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {filtered.length} of {firms.length}
          </span>
        )}
      </div>

      {/* Per-page toggle */}
      {!isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>Per page:</span>
          {[20, 50].map(n => (
            <button key={n} onClick={() => { setPageSize(n); setPage(1) }} style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--border)',
              background: pageSize === n ? 'var(--accent)' : 'var(--surface-raised)',
              color: pageSize === n ? '#fff' : 'var(--text-secondary)',
              fontWeight: pageSize === n ? 600 : 400,
            }}>{n}</button>
          ))}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    checked={paginated.length > 0 && paginated.every(f => checkedIds.has(f.firm_id))}
                    ref={el => { if (el) { const pids = paginated.map(f => f.firm_id); el.indeterminate = pids.some(id => checkedIds.has(id)) && !pids.every(id => checkedIds.has(id)) } }}
                    onChange={toggleCheckAll}
                    title="Select all visible"
                  />
                </th>
                <SortHeader col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Firm Name</SortHeader>
                <SortHeader col="type" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Type</SortHeader>
                <SortHeader col="hq" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>HQ</SortHeader>
                <SortHeader col="aum" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>AUM / Fund Size</SortHeader>
                <th>Owner</th>
                <th>Tags</th>
                <th>Direct Investment</th>
                <th>Investment Areas</th>
                <th>Contacts</th>
                <SortHeader col="notes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Notes</SortHeader>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{hasFilters ? 'No firms match your filters' : 'No firms yet'}</h3>
                    <p>{!hasFilters && 'Create your first firm to get started.'}</p>
                  </div>
                </td></tr>
              ) : (
                paginated.map(f => (
                  <tr
                    key={f.firm_id}
                    style={{
                      cursor: 'pointer',
                      background: checkedIds.has(f.firm_id) ? 'var(--accent-muted)' : undefined,
                    }}
                    onClick={() => setSelected(f)}
                  >
                    <td style={{ width: 36, paddingRight: 0 }} onClick={e => toggleCheck(f.firm_id, e)}>
                      <input
                        type="checkbox"
                        checked={checkedIds.has(f.firm_id)}
                        onChange={e => toggleCheck(f.firm_id, e)}
                        onClick={e => e.stopPropagation()}
                        style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ fontWeight: 500 }}>{f.name}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(f.firm_type ?? []).map(t => (
                          <span key={t} className={`badge ${FIRM_TYPE_BADGE[t] ?? 'badge-gray'}`}
                            style={{ fontSize: 10 }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.hq_location ?? '—'}</td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {formatAUM(f.aum_fund_size) ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {ownerName(f.relationship_owner)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(f.tags ?? []).slice(0, 3).map(t => (
                          <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>
                        ))}
                        {(f.tags ?? []).length > 3 && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            +{f.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {f.direct_investment_interest
                        ? <span className="badge badge-green" style={{ fontSize: 10 }}>Yes</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 160 }}>
                      {f.direct_investment_areas
                        ? <span title={f.direct_investment_areas}>
                            {f.direct_investment_areas.length > 40
                              ? f.direct_investment_areas.slice(0, 40) + '…'
                              : f.direct_investment_areas}
                          </span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      }
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 200 }}>
                      {(() => {
                        const cs = f.contacts ?? []
                        if (cs.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>
                        if (cs.length <= 3) return cs.map(c => c.name).join(', ')
                        return (
                          <>
                            {cs.slice(0, 2).map(c => c.name).join(', ')}
                            <span style={{ color: 'var(--text-muted)' }}> +{cs.length - 2} more</span>
                          </>
                        )
                      })()}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 }}>
                      {f.notes
                        ? <span title={f.notes}>{f.notes.length > 50 ? f.notes.slice(0, 50) + '...' : f.notes}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ width: 36, paddingRight: 8 }}>
                      <button
                        onClick={() => setToDelete(f)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                          display: 'inline-flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title="Delete firm"
                      ><TrashIcon /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination bar */}
      {!isLoading && filtered.length > 0 && (
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          pageStart={pageStart}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPage={setPage}
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title="New Firm" onClose={() => setCreate(false)}>
          <FirmForm
            users={users}
            onSubmit={createMutation.mutateAsync}
            onCancel={() => setCreate(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </Modal>
      )}

      {/* Detail modal */}
      {selected && (
        <FirmDetailModal
          firm={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => setSelected(null)}
          users={users}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.firm_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Merge modal */}
      {mergeOpen && checkedRecords.length >= 2 && (
        <MergeModal
          records={checkedRecords}
          entityType="firm"
          primaryId={mergePrimaryId}
          onPrimaryChange={setMergePrimaryId}
          onConfirm={() => mergeMutation.mutate({
            primary_id: mergePrimaryId,
            merge_ids:  checkedRecords.map(f => f.firm_id).filter(id => id !== mergePrimaryId),
          })}
          onCancel={() => { setMergeOpen(false); setMergeError(null) }}
          merging={mergeMutation.isPending}
          error={mergeError}
        />
      )}

      {/* Bulk-delete confirmation modal */}
      {bulkDeleteOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkDeleteOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {checkedIds.size} Firm{checkedIds.size !== 1 ? 's' : ''}?
            </h2>

            {/* Name list — up to 10 */}
            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {checkedRecords.slice(0, 10).map(f => (
                <li key={f.firm_id} style={{ marginBottom: 2 }}>{f.name}</li>
              ))}
              {checkedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>
                  …and {checkedRecords.length - 10} more
                </li>
              )}
            </ul>

            {/* Warning */}
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: '#f87171', marginBottom: 20, lineHeight: 1.5,
            }}>
              This will also remove these firms from any associated contacts' firm lists
              and pipeline entries. This action cannot be undone.
            </div>

            {/* Partial-failure result */}
            {bulkDeleteResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: '#f87171', marginBottom: 14,
              }}>
                {bulkDeleteResult.succeeded} deleted, {bulkDeleteResult.failed} failed.
                The failed records may have dependent data — delete them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={bulkDeleteFirms} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${checkedIds.size} Firm${checkedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
